import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material"
import { useToast } from "@crm-src/hooks/use-toast"
import { useAppSelector, useAppDispatch } from "@crm-src/lib/store"
import { updateUser } from "@crm-src/lib/store/authSlice"
import { PageHeader } from "@/components/ui/page-header"
import { Eye, EyeOff, User, Lock, Save } from "lucide-react"
import api from "@crm-src/lib/api"
// 2FA + SecuritySettings stripped — owned by the dashboard now.
import { useSearchParams } from "react-router-dom";
import { BackButton } from "@/components/ui/back-button"

interface ProfileFormData {
  first_name: string
  last_name: string
  email: string
  password: string
  password_confirmation: string
}

export default function ProfilePage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { toast } = useToast()
  const dispatch = useAppDispatch()
  const user = useAppSelector((state) => state.auth.user)

  // 2FA setup state stripped — dashboard owns 2FA enforcement.

  const [formData, setFormData] = useState<ProfileFormData>({
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    email: user?.email || "",
    password: "",
    password_confirmation: ""
  })
  
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [changePassword, setChangePassword] = useState(false)

  // Update form data when user data changes
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        email: user.email || ""
      }))
    }
  }, [user])

  // Helper function to get user initials
  const getUserInitials = () => {
    if (!user) return "U"
    
    if (user.first_name && user.last_name) {
      return `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase()
    } else if (user.full_name) {
      const names = user.full_name.split(" ")
      if (names.length >= 2) {
        return `${names[0].charAt(0)}${names[names.length - 1].charAt(0)}`.toUpperCase()
      }
      return user.full_name.charAt(0).toUpperCase()
    } else if (user.email) {
      return user.email.charAt(0).toUpperCase()
    }
    return "U"
  }

  const handleInputChange = (field: keyof ProfileFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validation
    if (!formData.first_name.trim() || !formData.last_name.trim() || !formData.email.trim()) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields.",
        variant: "destructive",
      })
      return
    }

    if (changePassword) {
      if (!formData.password) {
        toast({
          title: "Validation Error", 
          description: "Please enter a new password.",
          variant: "destructive",
        })
        return
      }
      
      if (formData.password !== formData.password_confirmation) {
        toast({
          title: "Validation Error",
          description: "Passwords do not match.",
          variant: "destructive",
        })
        return
      }
      
      if (formData.password.length < 8) {
        toast({
          title: "Validation Error",
          description: "Password must be at least 8 characters long.",
          variant: "destructive",
        })
        return
      }
    }

    setIsLoading(true)

    try {
      const updateData: any = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.trim()
      }

      if (changePassword) {
        updateData.password = formData.password
        updateData.password_confirmation = formData.password_confirmation
      }

      const response = await api.put('/profile', {
        user: updateData
      })

      // Update user in Redux store
      if (response.data.user) {
        dispatch(updateUser(response.data.user))
      }

      toast({
        title: "Success",
        description: response.data.message || "Profile updated successfully",
      })

      // Clear password fields
      setFormData(prev => ({
        ...prev,
        password: "",
        password_confirmation: ""
      }))
      setChangePassword(false)

    } catch (error: any) {
      console.error("Profile update failed:", error)
      
      const errorMessage = error.response?.data?.errors?.join(", ") || 
                          error.response?.data?.error ||
                          error.message || 
                          "Failed to update profile"
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  const getRoleDisplayName = () => {
    if (user?.is_super_admin) {
      return "Super Administrator"
    }
    
    if (user?.roles && user.roles.length > 0) {
      return user.roles.map(role => role.name).join(", ")
    }
    
    return "User"
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Edit Profile"
        description="Update your personal information and account settings"
        actions={
          <BackButton />
        }
      />

      <Box sx={{ maxWidth: "42rem" }}>
        <Card>
          <CardHeader
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar sx={{ width: 64, height: 64, fontSize: "1.125rem", fontWeight: 600, bgcolor: "#dbeafe", color: "#1e3a8a" }}>
                  {getUserInitials()}
                </Avatar>
                <Box>
                  <Typography component="span" sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 600 }}>
                    <User size={20} />
                    Profile Information
                  </Typography>
                  <Typography sx={{ color: "text.secondary" }}>
                    {getRoleDisplayName()}
                  </Typography>
                </Box>
              </Box>
            }
          />

          <CardContent>
            <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
                <TextField
                  id="first_name"
                  label="First Name *"
                  type="text"
                  size="small"
                  value={formData.first_name}
                  onChange={(e) => handleInputChange("first_name", e.target.value)}
                  placeholder="Enter your first name"
                  required
                />

                <TextField
                  id="last_name"
                  label="Last Name *"
                  type="text"
                  size="small"
                  value={formData.last_name}
                  onChange={(e) => handleInputChange("last_name", e.target.value)}
                  placeholder="Enter your last name"
                  required
                />
              </Box>

              <TextField
                id="email"
                label="Email Address *"
                type="email"
                size="small"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                placeholder="Enter your email address"
                required
              />

              <Divider />

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Box>
                    <Typography component="h3" sx={{ fontSize: "1.125rem", fontWeight: 500, display: "flex", alignItems: "center", gap: 1 }}>
                      <Lock size={20} />
                      Password Settings
                    </Typography>
                    <Typography sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
                      Leave blank to keep your current password
                    </Typography>
                  </Box>
                  <Button
                    type="button"
                    variant={changePassword ? "contained" : "outlined"}
                    color={changePassword ? "secondary" : "inherit"}
                    onClick={() => {
                      setChangePassword(!changePassword)
                      if (!changePassword) {
                        setFormData(prev => ({
                          ...prev,
                          password: "",
                          password_confirmation: ""
                        }))
                      }
                    }}
                  >
                    {changePassword ? "Cancel Change" : "Change Password"}
                  </Button>
                </Box>

                {changePassword && (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <TextField
                      id="password"
                      label="New Password *"
                      size="small"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => handleInputChange("password", e.target.value)}
                      placeholder="Enter new password (min 8 characters)"
                      inputProps={{ minLength: 8 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton size="small" type="button" onClick={() => setShowPassword(!showPassword)} edge="end">
                              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    <TextField
                      id="password_confirmation"
                      label="Confirm New Password *"
                      size="small"
                      type={showConfirmPassword ? "text" : "password"}
                      value={formData.password_confirmation}
                      onChange={(e) => handleInputChange("password_confirmation", e.target.value)}
                      placeholder="Confirm your new password"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton size="small" type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                              {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                )}
              </Box>

              <Divider />

              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isLoading}
                  startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <Save size={16} />}
                >
                  {isLoading ? "Updating..." : "Save Changes"}
                </Button>

                <Button
                  type="button"
                  variant="outlined"
                  color="inherit"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Security settings (2FA, passkeys, sessions) live in the
            dashboard's own profile area now. */}
      </Box>
    </Box>
  )
}
